import "react-app-polyfill/ie9";
import "react-app-polyfill/ie11";

import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";

import App from "./App";
import ErrorBoundary from "./shared/ErrorBoundary";
import Misconfiguration from "./shared/Misconfiguration";
import authStore from "./core/auth.store";
import * as serviceWorker from "./serviceWorker";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./index.css";

const FRIENDLY_ERROR_MSG = "Something went wrong. Please try again.";

// Register first so we run before React's error overlay. Prevents red screen; show user-friendly message instead.
window.addEventListener(
  "unhandledrejection",
  (event) => {
    const reason = event.reason;
    const isRequestError =
      reason &&
      (reason.isAxiosError === true ||
        (reason.response !== undefined && reason.config) ||
        (typeof reason.message === "string" && reason.message.includes("Request failed with status code")));
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!isRequestError) {
      try {
        toast(FRIENDLY_ERROR_MSG, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          type: "error",
        });
      } catch (_) {
        // ToastContainer may not be mounted yet; error still suppressed
      }
    }
  },
  true
);

// Production: require critical env vars so the app fails fast with a clear message instead of partial failures
const REQUIRED_ENV_PROD = ["REACT_APP_BASE_URL"];
const isProduction = process.env.NODE_ENV === "production";
const missingEnv = isProduction && REQUIRED_ENV_PROD.filter((key) => !process.env[key] || String(process.env[key]).trim() === "");
const hasMisconfiguration = missingEnv.length > 0;

const SUB_DOMAIN = process.env.REACT_APP_SUB_DOMAIN;

// React 18 API
const container = document.getElementById("root");
const root = createRoot(container);

// Suppress noisy React warnings from third-party defaultProps.
const originalConsoleError = console.error;
console.error = (...args) => {
  if (typeof args[0] === "string" &&
      args[0].includes("Support for defaultProps will be removed from function components")) {
    return;
  }
  if (typeof args[0] === "string" &&
      (args[0].includes("uses the legacy contextTypes API") ||
       args[0].includes("uses the legacy childContextTypes API"))) {
    return;
  }
  originalConsoleError(...args);
};

root.render(
  <Provider store={authStore}>
    {hasMisconfiguration ? (
      <Misconfiguration />
    ) : (
      <BrowserRouter basename={SUB_DOMAIN}>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    )}
  </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
